import { defineStore } from 'pinia';
import { iframeCommunication } from '@/utils/iframeCommunication';

export interface AuthState {
  token: string | null;
  account: string | null;
  loading: boolean;
  error: string | null;
}

function readAuthParamsFromWindow(): { token: string | null; account: string | null } {
  try {
    const params = new URLSearchParams(window.location.search);
    const rawToken = params.get('token');
    const rawAccount = params.get('account');

    return {
      token: rawToken ? rawToken.split('?')[0]?.trim() || null : null,
      account: rawAccount ? rawAccount.split('?')[0]?.trim() || null : null,
    };
  } catch {
    return { token: null, account: null };
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    account: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    getAuthHeaders: (state) => ({
      'auth-token': state.token,
      'auth-account': state.account,
    }),
  },

  actions: {
    async authenticate() {
      this.loading = true;
      this.error = null;

      try {
        const { token, account } = readAuthParamsFromWindow();

        // Apply credentials only when both appear in the URL. Do not clear
        // session/store here — iframe login uses parent-ready / session; clearing
        // on "missing URL params" breaks that and triggers spurious API/auth churn.
        if (!token || !account) {
          this.loading = false;
          return false;
        }

        // Set the token and account from query parameters
        this.token = token;
        this.account = account;
        
        // Store in sessionStorage for session persistence
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('auth_account', account);
        
        return true;
        
      } catch (e: unknown) {
        const error = e as { userMessage?: string; message?: string };
        this.error = error?.userMessage || error?.message || 'Authentication failed';
        this.loading = false;
        return false;
      } finally {
        this.loading = false;
      }
    },

    initializeFromSession() {
      const token = sessionStorage.getItem('auth_token');
      const account = sessionStorage.getItem('auth_account');
      
      // Set token if available
      if (token) {
        this.token = token;
      }
      
      // Set account if available (can be set independently)
      if (account) {
        this.account = account;
      }
    },

    /**
     * Align Pinia auth with the same URL + session rules as router beforeEach.
     * Use when the user acts without a navigation (e.g. open betslip) so the store
     * still reflects login applied via parent-ready, sessionStorage, or location bar
     * updates that Vue Router has not reconciled yet.
     */
    refreshAuthFromExternalSources() {
      const { token: urlToken, account: urlAccount } = readAuthParamsFromWindow();

      const sessionToken = sessionStorage.getItem('auth_token');
      const sessionAccount = sessionStorage.getItem('auth_account');

      if (urlToken) {
        sessionStorage.setItem('auth_token', urlToken);
        this.token = urlToken;
      } else if (sessionToken && !this.token) {
        this.token = sessionToken;
      }

      if (urlAccount) {
        sessionStorage.setItem('auth_account', urlAccount);
        this.account = urlAccount;
      } else if (sessionAccount && !this.account) {
        this.account = sessionAccount;
      }
    },

    /** Tenant slug for catalog/cutoff APIs: URL → session → Pinia (after refresh). */
    resolveTenantAccount(): string | null {
      this.refreshAuthFromExternalSources();
      return (
        this.account ||
        (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_account') : null) ||
        readAuthParamsFromWindow().account
      );
    },

    /**
     * Apply iframe parent-ready config synchronously before catalog API calls run.
     * Clears only the user token when absent — never wipes tenant account.
     */
    applyParentConfig(config: Record<string, unknown>) {
      const account = typeof config.account === 'string' ? config.account.trim() : '';
      if (account) {
        this.account = account;
        sessionStorage.setItem('auth_account', account);
      }

      const currency = typeof config.currency === 'string' ? config.currency.trim() : '';
      if (currency) {
        sessionStorage.setItem('auth_currency', currency);
      }

      const token = typeof config.token === 'string' ? config.token.trim() : '';
      if (token) {
        this.token = token;
        sessionStorage.setItem('auth_token', token);
      } else {
        this.clearInvalidTokenKeepAccount();
      }
    },

    /**
     * Drop only the user token; keep account (tenant) so theme/API context stays valid.
     * Does not notify the parent — avoids iframe reload on wagers when the user simply
     * needs to log in (parent handlers often refresh the whole iframe on session-invalid).
     */
    clearInvalidTokenKeepAccount() {
      this.token = null;
      sessionStorage.removeItem('auth_token');
    },

    // Method to clear invalid credentials when 401 error occurs
    clearInvalidCredentials() {
      this.token = null;
      sessionStorage.removeItem('auth_token');
      // Keep auth_account — tenant context (cutoffs, theme, catalog) is independent of user token.

      iframeCommunication.sendMessage('session-invalid', {
        reason: 'token-expired-or-invalid'
      });
    },

    logout(options?: { silent?: boolean; reason?: string }) {
      this.token = null;
      this.account = null;
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_account');
      if (!options?.silent) {
        iframeCommunication.sendMessage('logout', {
          reason: options?.reason ?? 'user-initiated'
        });
      }
    },

    // Method to check if we have valid session credentials
    hasValidSession() {
      const token = sessionStorage.getItem('auth_token');
      const account = sessionStorage.getItem('auth_account');
      return !!(token && account);
    },

    // Method to force re-initialization from session storage
    reinitializeFromSession() {
      this.initializeFromSession();
    },
  },
});
